import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { formatDate, formatMoney, formatNumber } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { Person } from '../ui/Avatar';
import { useMembers } from '../ui/UserSelect';
import { NewAccountDialog } from './NewAccountDialog';
import {
  bandTone,
  listAccounts,
  portfolioSummary,
  renewalTone,
  type AccountQuery,
  type CustomerAccount,
  type PortfolioSummary,
} from './api';

/**
 * The portfolio.
 *
 * Sorted worst first. A customer success list ordered alphabetically is a list where the account
 * about to leave sits under twenty that are fine, and the whole reason to open this screen is to
 * find the one that needs a call today.
 *
 * The figure that matters most is not the count of accounts — it is the money attached to the ones
 * in trouble. "Quatro contas em risco" is a statistic; "R$ 18.400 por mês em risco" is a decision.
 */

const TABS = ['risk', 'mine', 'renewals', 'unscored', 'all'] as const;

type Tab = (typeof TABS)[number];

function queryForTab(tab: Tab): AccountQuery {
  if (tab === 'risk') return { band: ['AT_RISK', 'CRITICAL'], sort: 'health' };
  if (tab === 'mine') return { mine: true, sort: 'health' };
  if (tab === 'renewals')
    return { renewalWithinDays: 60, renewalStatus: ['PENDING'], sort: 'renewal' };
  if (tab === 'unscored') return { sort: 'name' };

  return { sort: 'health' };
}

export function AccountsPage(): React.JSX.Element {
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('risk');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [adding, setAdding] = useState(false);

  const summary = useQuery({ queryKey: ['cs-summary'], queryFn: portfolioSummary });

  const query = useMemo<AccountQuery>(
    () => ({ ...queryForTab(tab), ...(applied ? { search: applied } : {}), limit: 100 }),
    [tab, applied],
  );

  const accounts = useQuery({
    queryKey: ['cs-accounts', tab, applied],
    queryFn: () => listAccounts(query),
  });

  const { options: people } = useMembers();

  const csm = (id: string | null): { name: string; url?: string | null; muted: boolean } => {
    const found = id === null ? undefined : people.find((person) => person.id === id);

    return found
      ? { name: found.name, url: found.avatarUrl, muted: false }
      : { name: t('cs.noCsm'), muted: true };
  };

  const rows = (accounts.data?.accounts ?? []).filter((account) =>
    tab === 'unscored' ? account.healthScore === null : true,
  );

  const columns: Column<CustomerAccount>[] = [
    {
      key: 'account',
      header: t('cs.account'),
      text: (account) =>
        `${account.companyName} ${
          account.renewalStatus === 'CHURNED' ? (account.churnReason ?? '') : ''
        }`.trim(),
      render: (account) => (
        <span className="cell-stack">
          <span className="strong">{account.companyName}</span>
          {account.renewalStatus === 'CHURNED' && (
            <span className="muted small">{account.churnReason ?? '—'}</span>
          )}
        </span>
      ),
    },
    {
      key: 'health',
      header: t('cs.health'),
      text: (account) =>
        account.healthScore === null
          ? t('cs.notScored')
          : `${account.healthScore} ${t(`cs.band.${account.healthBand ?? 'WATCH'}`)}`,
      render: (account) => <HealthCell account={account} />,
    },
    {
      key: 'mrr',
      header: t('cs.mrr'),
      align: 'end',
      text: (account) => (account.mrrCents === null ? '—' : formatMoney(account.mrrCents)),
      render: (account) =>
        account.mrrCents === null ? (
          <span className="muted">—</span>
        ) : (
          formatMoney(account.mrrCents)
        ),
    },
    {
      key: 'renewal',
      header: t('cs.renewal'),
      text: (account) =>
        account.renewalDate === null
          ? '—'
          : `${t(`cs.renewal.${account.renewalStatus}`)} ${formatDate(account.renewalDate)}`,
      render: (account) =>
        account.renewalDate === null ? (
          <span className="muted small">—</span>
        ) : (
          <span className="cell-stack">
            <Badge tone={renewalTone(account)}>{t(`cs.renewal.${account.renewalStatus}`)}</Badge>
            <span className="muted small">{formatDate(account.renewalDate)}</span>
          </span>
        ),
    },
    {
      key: 'csm',
      header: t('cs.csm'),
      text: (account) => csm(account.csmId).name,
      render: (account) => <Person size="xs" {...csm(account.csmId)} />,
    },
    {
      key: 'since',
      header: t('cs.since'),
      text: (account) => formatDate(account.startedAt),
      render: (account) => <span className="muted small">{formatDate(account.startedAt)}</span>,
    },
    // Off by default; the chooser turns them on and the account remembers the choice.
    {
      key: 'churnReason',
      header: t('cs.churnReason'),
      hidden: true,
      text: (account) => account.churnReason ?? '—',
      render: (account) => account.churnReason ?? '—',
    },
    {
      key: 'notes',
      header: t('record.field.notes'),
      hidden: true,
      text: (account) => account.notes ?? '—',
      render: (account) => <span className="muted small">{account.notes ?? '—'}</span>,
    },
    {
      key: 'healthComputedAt',
      header: t('cs.scoredOn'),
      hidden: true,
      text: (account) => formatDate(account.healthComputedAt),
      render: (account) => formatDate(account.healthComputedAt),
    },
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      hidden: true,
      text: (account) => formatDate(account.createdAt),
      render: (account) => formatDate(account.createdAt),
    },
  ];

  return (
    <div className="view view--wide">
      <PageHeader
        title={t('nav.accounts')}
        description={t('cs.description')}
        actions={
          <button
            type="button"
            className="button button--primary button--compact"
            onClick={() => setAdding(true)}
          >
            {t('cs.manageCompany')}
          </button>
        }
      />

      <NewAccountDialog open={adding} onClose={() => setAdding(false)} />

      {summary.isLoading ? (
        <Loading />
      ) : summary.error ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : summary.data ? (
        <Figures
          summary={summary.data}
          onShowRisk={() => setTab('risk')}
          onShowRenewals={() => setTab('renewals')}
        />
      ) : null}

      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(search.trim());
        }}
      >
        <span className="cell-inline">
          {TABS.map((key) => (
            <button
              key={key}
              type="button"
              className={`chip${tab === key ? ' chip--active' : ''}`}
              onClick={() => setTab(key)}
            >
              {t(`cs.tab.${key}`)}
            </button>
          ))}
        </span>

        <label className="field-search">
          <input
            type="search"
            value={search}
            placeholder={t('cs.searchPlaceholder')}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <button type="submit" className="button button--quiet button--compact">
          {t('crm.search')}
        </button>

        {accounts.data && (
          <span className="muted small">
            {t('crm.results').replace('{count}', String(rows.length))}
          </span>
        )}
      </form>

      <Panel>
        <DataTable
          tableKey="cs-accounts"
          rows={rows}
          columns={columns}
          rowKey={(account) => account.id}
          isLoading={accounts.isLoading}
          error={accounts.error}
          onRetry={() => void accounts.refetch()}
          onRowClick={(account) => navigate(`/sucesso/contas/${account.id}`)}
          empty={{ title: t('cs.noAccounts') }}
        />
      </Panel>
    </div>
  );
}

/**
 * The score as a bar, with the band as the colour.
 *
 * A number on its own says nothing about whether 62 is good. The bar answers that at a glance, and
 * an account nobody has scored shows an empty track rather than a zero — "we do not know" and "it is
 * terrible" are not the same thing and must not look the same.
 */
function HealthCell({ account }: { account: CustomerAccount }): React.JSX.Element {
  if (account.healthScore === null) {
    return <span className="muted small">{t('cs.notScored')}</span>;
  }

  return (
    <span className="health">
      <span className="health__track">
        <span
          className={`health__fill health__fill--${account.healthBand?.toLowerCase() ?? 'watch'}`}
          style={{ width: `${account.healthScore}%` }}
        />
      </span>
      <span className="health__value">{account.healthScore}</span>
      <Badge tone={bandTone(account.healthBand)}>
        {t(`cs.band.${account.healthBand ?? 'WATCH'}`)}
      </Badge>
    </span>
  );
}

function Figures({
  summary,
  onShowRisk,
  onShowRenewals,
}: {
  summary: PortfolioSummary;
  onShowRisk: () => void;
  onShowRenewals: () => void;
}): React.JSX.Element {
  const atRisk = summary.byBand.AT_RISK + summary.byBand.CRITICAL;

  return (
    <div className="metrics">
      <button
        type="button"
        className={`metric metric--action${summary.atRiskMrrCents > 0 ? ' metric--alert' : ''}`}
        onClick={onShowRisk}
      >
        <span className="metric__label">{t('cs.kpi.atRiskRevenue')}</span>
        <span className="metric__value">{formatMoney(summary.atRiskMrrCents)}</span>
        <span className="muted small">
          {t('cs.kpi.atRiskAccounts').replace('{n}', String(atRisk))}
        </span>
      </button>

      <button
        type="button"
        className={`metric metric--action${summary.overdueRenewals > 0 ? ' metric--alert' : ''}`}
        onClick={onShowRenewals}
      >
        <span className="metric__label">{t('cs.kpi.renewals')}</span>
        <span className="metric__value">{formatNumber(summary.renewingIn30Days)}</span>
        <span className="muted small">
          {summary.overdueRenewals > 0
            ? t('cs.kpi.renewalsOverdue').replace('{n}', String(summary.overdueRenewals))
            : t('cs.kpi.renewalsClear')}
        </span>
      </button>

      <Figure label={t('cs.kpi.mrr')} value={formatMoney(summary.mrrCents)} />

      <Figure
        label={t('cs.kpi.managed')}
        value={formatNumber(summary.managed)}
        hint={
          summary.unscored > 0
            ? t('cs.kpi.unscored').replace('{n}', String(summary.unscored))
            : undefined
        }
      />

      <Figure label={t('cs.kpi.healthy')} value={formatNumber(summary.byBand.HEALTHY)} />

      <Figure
        label={t('cs.kpi.churned')}
        value={formatNumber(summary.churnedThisQuarter)}
        hint={t('cs.kpi.thisQuarter')}
      />
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="metric">
      <span className="metric__label">{label}</span>
      <span className="metric__value">{value}</span>
      {hint && <span className="muted small">{hint}</span>}
    </div>
  );
}
